import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, AlertCircle, TrendingUp, TrendingDown, Minus, Download, Settings, RotateCcw, Save, Plus } from 'lucide-react';
import typeformService from '@/lib/typeform';
import { parseTypeformResponses, getNPSChartData, getSpecialQuestionNumbers } from '@/lib/typeformParser';
import { useQuestionLayout } from '@/hooks/useQuestionLayout';
import { DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import DraggableSection from './DraggableSection';
import DraggableQuestionCard from './DraggableQuestionCard';
import { fetchProgramBySO, fetchSessions, fetchAttendees } from '@/lib/airtable';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/use-toast';

/**
 * SessionReport Component
 * Displays survey responses for a specific session
 * Part of Issue #128: Create basic session report page
 */
const SessionReport = () => {
  const { soNumber, sessionId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );
  
  // Active drag item for overlay
  const [activeDragItem, setActiveDragItem] = useState(null);
  
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [programData, setProgramData] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [formDefinition, setFormDefinition] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [responseStats, setResponseStats] = useState({
    total: 0,
    totalAttendees: 0
  });

  // Load program and session data
  useEffect(() => {
    const loadData = async () => {
      if (!soNumber || !sessionId) {
        setError('Missing SO number or session ID');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch program data
        const program = await fetchProgramBySO(soNumber);
        setProgramData(program);

        // Fetch sessions
        const sessions = await fetchSessions(program.soid);
        const session = sessions.find(s => s.sessionId === sessionId);
        
        if (!session) {
          setError('Session not found');
          setLoading(false);
          return;
        }
        
        setSessionData(session);

        // Fetch attendees to get total count
        const attendees = await fetchAttendees(program.soid, program.so);
        const totalAttendees = attendees.length;

        // Fetch Typeform responses if survey link exists
        if (session.surveyLink) {
          const formId = typeformService.extractFormIdFromUrl(session.surveyLink);
          if (formId) {
            // Fetch form definition and responses in parallel
            let [definition, responses] = await Promise.all([
              typeformService.getFormDefinition(formId),
              typeformService.getFormResponsesByCohort(formId, soNumber)
            ]);
            
            // Retry once if we get 0 responses (sometimes Typeform API needs a moment)
            if (responses.total_items === 0 && !responses.cohort_mismatch) {
              if (import.meta.env.DEV) {
                console.log('Got 0 responses on first try, retrying after clearing cache...');
              }
              typeformService.clearCache(formId);

              // Wait a moment and retry
              await new Promise(resolve => setTimeout(resolve, 1000));
              responses = await typeformService.getFormResponsesByCohort(formId, soNumber);
              if (import.meta.env.DEV) {
                console.log('Retry result:', responses.total_items, 'responses');
              }
            }
            
            setFormDefinition(definition);
            
            // Parse the responses using the definition
            if (definition && responses) {
              const parsed = parseTypeformResponses(definition, responses);
              setParsedData(parsed);
              
              // Development-only logging for debugging
              if (import.meta.env.DEV) {
                console.log('Form definition:', definition);
                console.log('Filtered responses for SO', soNumber, ':', responses);
                console.log('Parsed data:', parsed);
                
                // Log field types to debug why NPS/ratings aren't detected
                if (definition.fields) {
                  console.log('Survey questions:');
                  definition.fields.forEach(field => {
                    console.log(`- ${field.title} (type: ${field.type}, props:`, field.properties, ')');
                  });
                }
                
                // Log first response structure if available
                if (responses.items && responses.items.length > 0) {
                  console.log('First response answers:', responses.items[0].answers);
                }
                
                // Debug parsed data
                console.log('Parsed NPS data:', parsed.nps);
                console.log('Parsed ratings:', parsed.ratings);
                console.log('All parsed questions:', parsed.questions);
              }
            }
            
            // Calculate stats
            setResponseStats({
              total: responses.total_items || 0,
              totalAttendees: totalAttendees,
              cohortMismatch: responses.cohort_mismatch || false,
              actualCohort: responses.actual_cohort || null
            });
          }
        } else {
          // Set attendee count even if no survey
          setResponseStats(prev => ({
            ...prev,
            totalAttendees: totalAttendees
          }));
        }
      } catch (err) {
        console.error('Error loading session report:', err);
        setError(err.message || 'Failed to load session report');
        toast({
          title: "Error",
          description: "Failed to load session report data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [soNumber, sessionId, toast]);


  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get dynamic question numbers
  const questionNumbers = useMemo(() => {
    return parsedData && formDefinition ? getSpecialQuestionNumbers(parsedData, formDefinition) : {
      npsQuestionNumber: null,
      ratingQuestionNumbers: []
    };
  }, [parsedData, formDefinition]);
  
  // Helper function to check if question is NPS or rating (moved up to avoid reference error)
  const isNPSOrRatingQuestion = (question, data) => {
    // Check if this question is already displayed in the NPS or ratings sections above
    const isNPS = data?.nps?.fieldId === question.fieldId;
    const isRating = data?.ratings?.some(r => r.fieldId === question.fieldId);
    
    return isNPS || isRating;
  };

  // Question layout management
  const filteredQuestions = useMemo(() => {
    return parsedData?.questions?.filter(q => q.type !== 'statement' && !isNPSOrRatingQuestion(q, parsedData)) || [];
  }, [parsedData]);
  
  const {
    layout,
    customizeMode,
    setCustomizeMode,
    saveLayout,
    resetLayout,
    moveQuestion,
    reorderSections,
    addSection,
    renameSection,
    setSectionColumns,
    isLayoutReady
  } = useQuestionLayout(soNumber, sessionId, filteredQuestions);
  
  // Drag and drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveDragItem(active.data.current);
  };
  
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragItem(null);
    
    if (!over) return;
    
    const activeData = active.data.current;
    const overData = over.data.current;
    
    if (activeData.type === 'question') {
      if (overData.type === 'section') {
        // Moving question to a different section
        const questionId = activeData.question.fieldId;
        const fromSection = activeData.sectionId;
        const toSection = overData.sectionId;
        
        if (fromSection !== toSection) {
          moveQuestion(questionId, fromSection, toSection, 0);
        }
      } else if (overData.type === 'question') {
        // Reordering questions within or between sections
        const activeQuestionId = activeData.question.fieldId;
        const overQuestionId = overData.question.fieldId;
        const fromSection = activeData.sectionId;
        const toSection = overData.sectionId;
        
        if (fromSection === toSection) {
          // Same section - reorder
          const sectionQuestions = layout.sections[fromSection];
          const oldIndex = sectionQuestions.findIndex(q => q.fieldId === activeQuestionId);
          const newIndex = sectionQuestions.findIndex(q => q.fieldId === overQuestionId);
          
          if (oldIndex !== newIndex) {
            // Use the existing moveQuestion function to handle reordering
            const question = sectionQuestions[oldIndex];
            moveQuestion(question.fieldId, fromSection, fromSection, newIndex);
          }
        } else {
          // Different sections - move to position
          const toSectionQuestions = layout.sections[toSection];
          const newIndex = toSectionQuestions.findIndex(q => q.fieldId === overQuestionId);
          moveQuestion(activeQuestionId, fromSection, toSection, newIndex);
        }
      }
    } else if (activeData.type === 'section') {
      // Reordering sections
      const activeSectionId = activeData.sectionId;
      const overSectionId = overData.sectionId;
      
      const oldIndex = layout.sectionOrder.indexOf(activeSectionId);
      const newIndex = layout.sectionOrder.indexOf(overSectionId);
      
      if (oldIndex !== newIndex) {
        const newSectionOrder = arrayMove(layout.sectionOrder, oldIndex, newIndex);
        reorderSections(newSectionOrder);
      }
    }
  };
  
  const handleAddSection = () => {
    const newSectionName = prompt('Enter section name:');
    if (newSectionName && newSectionName.trim()) {
      addSection(newSectionName.trim());
    }
  };

  // Process survey responses for NPS calculation
  const npsData = useMemo(() => {
    // Use real parsed NPS data if available
    if (parsedData?.nps) {
      const nps = parsedData.nps;
      return {
        score: nps.score || 0,
        promoters: nps.promoters || 0,
        passives: nps.passives || 0,
        detractors: nps.detractors || 0,
        chartData: getNPSChartData(nps),
        total: nps.total || 0,
        promoterCount: nps.promoterCount || 0,
        passiveCount: nps.passiveCount || 0,
        detractorCount: nps.detractorCount || 0
      };
    }
    
    // Default empty state
    return { 
      score: 0, 
      promoters: 0, 
      passives: 0, 
      detractors: 0, 
      chartData: [{ name: 'NPS', value: 0, fill: '#94a3b8' }],
      total: 0,
      promoterCount: 0,
      passiveCount: 0,
      detractorCount: 0
    };
  }, [parsedData]);

  // Process rating questions for bar chart
  const ratingsData = useMemo(() => {
    // Use real parsed rating data if available
    if (parsedData?.ratings && parsedData.ratings.length > 0) {
      // Format for the chart component
      const data = parsedData.ratings.map(rating => ({
        question: rating.question, // Show full question text
        rating: rating.rating || 0,
        responses: rating.responses || 0
      }));
      
      // Development-only logging
      if (import.meta.env.DEV) {
        console.log('Real ratings data for chart:', data);
        console.log('First rating item:', data[0]);
        
        // Test data to verify chart works
        const testData = [
          { question: "Test Question 1", rating: 4.5, responses: 10 },
          { question: "Test Question 2", rating: 3.8, responses: 8 }
        ];
        console.log('Test data:', testData);
        // Uncomment next line to test with fake data:
        // return testData;
      }
      
      return data;
    }
    
    // Return empty array if no rating questions found
    return [];
  }, [parsedData]);

  // Functions moved up to avoid reference errors

  const exportSingleQuestion = (question) => {
    // Prepare CSV data
    const headers = ['SO Number', 'Session ID', 'Program Name', 'Session Date', 'Question', 'Question Type', 'Response Count', 'Response'];
    const rows = [];
    
    // Add header row
    rows.push(headers.join(','));
    
    // Add data for each response
    if (question.responses && question.responses.length > 0) {
      question.responses.forEach(response => {
        const row = [
          soNumber || '',
          sessionId || '',
          String(programData?.programName || '').replace(/"/g, '""'), // Escape quotes
          sessionData?.start || '',
          String(question.question || '').replace(/"/g, '""'), // Escape quotes
          question.type || '',
          question.responseCount || 0,
          String(typeof response === 'string' ? response : JSON.stringify(response)).replace(/"/g, '""') // Escape quotes
        ].map(field => `"${field}"`); // Wrap in quotes
        
        rows.push(row.join(','));
      });
    } else {
      // If no responses, add one row with empty response
      const row = [
        soNumber || '',
        sessionId || '',
        String(programData?.programName || '').replace(/"/g, '""'),
        sessionData?.start || '',
        String(question.question || '').replace(/"/g, '""'),
        question.type || '',
        question.responseCount || 0,
        ''
      ].map(field => `"${field}"`);
      
      rows.push(row.join(','));
    }
    
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `question_${question.fieldId}_${soNumber}_${sessionId}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Exported!",
      description: `Question data exported as CSV successfully`,
    });
  };

  const exportAllQuestions = () => {
    if (!parsedData?.questions) return;
    
    // Format CSV like Typeform exports - with response ID and timestamps
    const questions = parsedData.questions.filter(q => q.type !== 'statement');
    
    // Create headers like Typeform: Response ID, Submit Date, then each question
    const headers = [
      'Response ID',
      'Submit Date', 
      'SO Number',
      'Session ID',
      'Program Name',
      ...questions.map((q, idx) => `${q.question} (Q${idx + 1})`)
    ];
    
    const rows = [];
    rows.push(headers.join(','));
    
    // Get all unique responses by combining all question responses
    const allResponses = new Set();
    questions.forEach(question => {
      if (question.responses) {
        question.responses.forEach((_, responseIndex) => {
          allResponses.add(responseIndex);
        });
      }
    });
    
    // Create one row per response (like Typeform)
    Array.from(allResponses).forEach(responseIndex => {
      const responseId = `${soNumber}-${sessionId}-${responseIndex + 1}`;
      const submitDate = sessionData?.start || new Date().toISOString();
      
      const row = [
        responseId,
        submitDate,
        soNumber || '',
        sessionId || '',
        String(programData?.programName || '').replace(/"/g, '""')
      ];
      
      // Add response for each question (empty if no response for this response index)
      questions.forEach(question => {
        const response = question.responses && question.responses[responseIndex] 
          ? String(question.responses[responseIndex]).replace(/"/g, '""')
          : '';
        row.push(response);
      });
      
      rows.push(row.map(field => `"${field}"`).join(','));
    });
    
    // If no responses, add one empty row
    if (allResponses.size === 0) {
      const emptyRow = [
        `${soNumber}-${sessionId}-empty`,
        sessionData?.start || new Date().toISOString(),
        soNumber || '',
        sessionId || '',
        String(programData?.programName || '').replace(/"/g, '""'),
        ...questions.map(() => '')
      ];
      rows.push(emptyRow.map(field => `"${field}"`).join(','));
    }
    
    const csvContent = rows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Create filename with SO, date, and client
    const sessionDate = sessionData?.start ? new Date(sessionData.start).toISOString().split('T')[0] : 'unknown-date';
    const clientName = String(programData?.clientName || 'unknown-client').replace(/[^a-zA-Z0-9-_]/g, '-').toLowerCase();
    const fileName = `${soNumber}_${sessionDate}_${clientName}_responses.csv`;
    
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "CSV Exported!",
      description: `Survey responses saved as ${fileName}`,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" className="mr-2" />
        <span className="text-lg text-muted-foreground">Loading session report...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Error Loading Report</h2>
        <p className="text-muted-foreground mb-4">{error}</p>
        <Button onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      {/* Header */}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate(`/${soNumber}`)}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to SO
        </Button>
        
        <h1 className="text-2xl font-bold mb-2">
          Session Survey Report
        </h1>
        <p className="text-muted-foreground">
          {programData?.programName} - Session {sessionData?.sessionNo}
        </p>
      </div>

      {/* Session Info Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Session Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Date & Time</p>
              <p className="text-sm">
                {formatDate(sessionData?.start)}
              </p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">Program Leader</p>
              <p className="text-sm">
                {programData?.plName || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">Client</p>
              <p className="text-sm">
                {programData?.clientName || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">Session Type</p>
              <p className="text-sm">
                {sessionData?.type || programData?.type || 'N/A'}
              </p>
            </div>
            
            <div>
              <p className="text-sm font-medium text-muted-foreground">Response Rate</p>
              <p className="text-sm">
                {responseStats.total}/{responseStats.totalAttendees} responses
                {responseStats.totalAttendees > 0 && 
                  ` (${Math.round((responseStats.total / responseStats.totalAttendees) * 100)}%)`
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Survey Responses Card */}
      <Card>
        <CardHeader>
          <CardTitle>Survey Responses</CardTitle>
        </CardHeader>
        <CardContent>
          {!sessionData?.surveyLink ? (
            <div className="text-center py-8 text-muted-foreground">
              No survey configured for this session
            </div>
          ) : responseStats.total === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No responses received yet
            </div>
          ) : (
            <div className="space-y-6">
              {/* Cohort Mismatch Warning */}
              {responseStats.cohortMismatch && (
                <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    ⚠️ Survey Configuration Notice
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    These survey responses were collected with SO #{responseStats.actualCohort} but are being shown for SO #{soNumber}.
                    This may indicate the survey link was configured with an incorrect SO number.
                  </p>
                </div>
              )}
              
              {/* Response Summary - Horizontal */}
              <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Response Summary</p>
                  <div className="text-xl font-bold">
                    {responseStats.total} {responseStats.total === 1 ? 'Response' : 'Responses'}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Out of {responseStats.totalAttendees} registered</p>
                  {responseStats.totalAttendees > 0 && (
                    <p className="text-sm font-semibold">
                      ({Math.round((responseStats.total / responseStats.totalAttendees) * 100)}% response rate)
                    </p>
                  )}
                </div>
              </div>

              {/* NPS Score Section - Only show if NPS question exists */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* NPS Gauge Chart */}
                {parsedData?.nps && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        Net Promoter Score
                        {questionNumbers.npsQuestionNumber && ` (Q${questionNumbers.npsQuestionNumber})`}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pb-4">
                    {/* Compact NPS Layout */}
                    <div className="flex items-center justify-between">
                      {/* Left: Large NPS Score */}
                      <div className="flex-shrink-0">
                        <div className="text-center">
                          <div className="text-6xl font-bold mb-1" style={{ color: npsData.chartData[0]?.fill }}>
                            {npsData.score}
                          </div>
                          <div className="text-sm text-muted-foreground font-medium">
                            NPS Score
                          </div>
                        </div>
                      </div>
                      
                      {/* Right: Visual gauge */}
                      <div className="flex-1 ml-6">
                        <div className="relative">
                          {/* Background track */}
                          <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                            {/* Score fill */}
                            <div 
                              className="h-full rounded-full transition-all duration-1000 ease-out"
                              style={{ 
                                width: `${Math.abs(npsData.score)}%`,
                                backgroundColor: npsData.chartData[0]?.fill
                              }}
                            />
                          </div>
                          
                          {/* Scale markers */}
                          <div className="flex justify-between text-xs text-muted-foreground mt-1">
                            <span>-100</span>
                            <span>0</span>
                            <span>+100</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* NPS Breakdown - Compact */}
                    <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 rounded bg-green-50 dark:bg-green-950">
                        <p className="text-xs text-muted-foreground">Promoters</p>
                        <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                          {npsData.promoterCount || 0} ({npsData.promoters || 0}%)
                        </p>
                      </div>
                      <div className="p-2 rounded bg-yellow-50 dark:bg-yellow-950">
                        <p className="text-xs text-muted-foreground">Passives</p>
                        <p className="text-sm font-semibold text-yellow-600 dark:text-yellow-400">
                          {npsData.passiveCount || 0} ({npsData.passives || 0}%)
                        </p>
                      </div>
                      <div className="p-2 rounded bg-red-50 dark:bg-red-950">
                        <p className="text-xs text-muted-foreground">Detractors</p>
                        <p className="text-sm font-semibold text-red-600 dark:text-red-400">
                          {npsData.detractorCount || 0} ({npsData.detractors || 0}%)
                        </p>
                      </div>
                    </div>
                    
                    {/* Overall Rating Summary - matches the style from Average Ratings card */}
                    {parsedData?.ratings && parsedData.ratings.length > 0 && (() => {
                      const avg = parsedData.ratings.reduce((acc, curr) => acc + (curr.rating || 0), 0) / parsedData.ratings.length;
                      const percentage = Math.round((avg / 5) * 100);
                      const icon = avg >= 4.5 
                        ? <TrendingUp className="h-5 w-5 text-green-500" />
                        : avg >= 3.5 
                        ? <Minus className="h-5 w-5 text-yellow-500" />
                        : <TrendingDown className="h-5 w-5 text-red-500" />;
                      const label = avg >= 4.5 ? 'Excellent' : avg >= 3.5 ? 'Good' : 'Needs Improvement';
                      
                      return (
                        <div className="mt-3 flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <p className="text-xs text-muted-foreground">Overall Average</p>
                            <p className="text-lg font-semibold">
                              {avg.toFixed(1)} / 5.0 ({percentage}%)
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {icon}
                            <span className="text-sm font-medium">{label}</span>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
                )}

                {/* Rating Bars Chart - Only show if rating questions exist */}
                {parsedData?.ratings && parsedData.ratings.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">
                        Average Ratings
                        {questionNumbers.ratingQuestionNumbers.length > 0 && 
                          ` (Q${questionNumbers.ratingQuestionNumbers.join(', Q')})`
                        }
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pb-4">
                    <div className="space-y-3">
                      {ratingsData.map((item, index) => (
                        <div key={index} className="space-y-1.5">
                          {/* Question text above the bar */}
                          <div className="text-sm font-medium text-foreground px-1">
                            {item.question}
                          </div>
                          
                          {/* Rating bar */}
                          <div className="flex items-center gap-3">
                            <div className="flex-1 bg-muted rounded-lg overflow-hidden h-7">
                              <div 
                                className="h-full bg-blue-500 flex items-center justify-end pr-3 transition-all duration-500"
                                style={{ width: `${(item.rating / 5) * 100}%` }}
                              >
                                <span className="text-white text-sm font-semibold">
                                  {item.rating}
                                </span>
                              </div>
                            </div>
                            <div className="text-sm text-muted-foreground min-w-[60px]">
                              {item.responses} resp.
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Rating Summary */}
                    <div className="mt-3 flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <div>
                        <p className="text-xs text-muted-foreground">Overall Average</p>
                        <p className="text-lg font-semibold">
                          {(() => {
                            if (ratingsData.length === 0) return '0.0 / 5.0 (0%)';
                            const avg = ratingsData.reduce((acc, curr) => acc + (curr.rating || 0), 0) / ratingsData.length;
                            const percentage = Math.round((avg / 5) * 100);
                            return `${avg.toFixed(1)} / 5.0 (${percentage}%)`;
                          })()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {ratingsData.length > 0 && (() => {
                          const avg = ratingsData.reduce((acc, curr) => acc + (curr.rating || 0), 0) / ratingsData.length;
                          return (
                            <>
                              {avg >= 4.5 ? (
                                <TrendingUp className="h-5 w-5 text-green-500" />
                              ) : avg >= 3.5 ? (
                                <Minus className="h-5 w-5 text-yellow-500" />
                              ) : (
                                <TrendingDown className="h-5 w-5 text-red-500" />
                              )}
                              <span className="text-sm font-medium">
                                {avg >= 4.5 
                                  ? 'Excellent'
                                  : avg >= 3.5
                                  ? 'Good'
                                  : 'Needs Improvement'
                                }
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </CardContent>
                </Card>
                )}
              </div>
              
              {/* All Questions Section */}
              {parsedData?.questions && parsedData.questions.length > 0 && (
                <div className="mt-8">
                  {/* Control Buttons at Top */}
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-1">Question-by-Question Analysis</h3>
                      <p className="text-sm text-muted-foreground">
                      </p>
                    </div>
                    
                    {/* Export and Customize Controls */}
                    <div className="flex items-center gap-3">
                      {/* Export Button */}
                      <Button
                        variant="outline"
                        onClick={exportAllQuestions}
                        className="flex items-center gap-2 min-h-[44px]"
                        title="Export all questions as CSV"
                      >
                        <Download className="h-4 w-4" />
                        Export CSV
                      </Button>
                      
                      {/* Customize Layout Controls */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant={customizeMode ? "default" : "outline"}
                          onClick={() => setCustomizeMode(!customizeMode)}
                          className="flex items-center gap-2 min-h-[44px]"
                        >
                          <Settings className="h-4 w-4" />
                          {customizeMode ? 'Done' : 'Customize'}
                        </Button>
                        
                        {customizeMode && (
                          <>
                            <Button
                              variant="outline"
                              onClick={resetLayout}
                              className="flex items-center gap-2 min-h-[44px]"
                              title="Reset to default layout"
                            >
                              <RotateCcw className="h-4 w-4" />
                              Reset
                            </Button>
                            <Button
                              variant="outline"
                              onClick={saveLayout}
                              className="flex items-center gap-2 min-h-[44px]"
                              title="Save current layout"
                            >
                              <Save className="h-4 w-4" />
                              Save
                            </Button>
                            <Button
                              variant="outline"
                              onClick={handleAddSection}
                              className="flex items-center gap-2 min-h-[44px]"
                              title="Add new section"
                            >
                              <Plus className="h-4 w-4" />
                              Section
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* All Questions in Original Form Order - NO FILTERING */}
                  <div className="space-y-4">
                    {/* Drag and Drop Question Layout */}
                  {isLayoutReady && (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext 
                        items={layout.sectionOrder.map(s => `section-${s}`)}
                        strategy={verticalListSortingStrategy}
                      >
                        {layout.sectionOrder.map((sectionName) => (
                          <DraggableSection
                            key={sectionName}
                            sectionId={sectionName}
                            sectionName={sectionName}
                            questions={layout.sections[sectionName] || []}
                            isCustomizeMode={customizeMode}
                            onRenameSection={renameSection}
                            onSetSectionColumns={setSectionColumns}
                            sectionColumns={layout.sectionColumns?.[sectionName] || 1}
                            formDefinition={formDefinition}
                            totalQuestions={parsedData.metadata?.totalQuestions || parsedData.questions?.length || 0}
                            onExport={exportSingleQuestion}
                          />
                        ))}
                      </SortableContext>
                      
                      {/* Drag Overlay */}
                      <DragOverlay>
                        {activeDragItem ? (
                          activeDragItem.type === 'question' ? (
                            <div className="opacity-90">
                              <DraggableQuestionCard
                                question={activeDragItem.question}
                                index={0}
                                totalQuestions={parsedData.metadata?.totalQuestions || parsedData.questions?.length || 0}
                                onExport={() => {}}
                                isCustomizeMode={false}
                                sectionId=""
                              />
                            </div>
                          ) : (
                            <div className="bg-background border rounded-lg p-3 shadow-lg opacity-90">
                              <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                                {activeDragItem.sectionId}
                              </h4>
                            </div>
                          )
                        ) : null}
                      </DragOverlay>
                    </DndContext>
                  )}
                  </div>
                </div>
              )}
              
              {/* Debug info in development - ORIGINAL TYPEFORM QUESTIONS */}
              {import.meta.env.DEV && formDefinition?.fields && (
                <details className="mt-6">
                  <summary className="cursor-pointer p-4 bg-muted/50 rounded-lg hover:bg-muted/60 transition-colors">
                    <span className="text-sm font-bold text-muted-foreground">
                      ORIGINAL TYPEFORM QUESTIONS IN ORDER
                    </span>
                  </summary>
                  <div className="mt-2 p-4 bg-muted/30 rounded-lg space-y-1">
                    {formDefinition.fields.map((field, index) => (
                      <p key={field.id} className="text-xs font-mono text-muted-foreground">
                        Q{index + 1}: {field.title} (type: {field.type})
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SessionReport;